import { useEffect, useRef, useState } from 'react'
import { SeenKeyRing } from './notification-dedupe.ts'
import { showNotification } from './notify.ts'

/**
 * The notification watcher, as a hook. D11 is the spec, and every line
 * below is one of its clauses.
 *
 * Ported from the old vault's `NotificationWatcher.tsx`, which is the
 * proven shape: one poll loop, a source per thing worth interrupting
 * someone for, each in its own try/catch, dedupe in `localStorage`, OS
 * notifications only while the tab is hidden. What changed in the port is
 * the separation — the old component mixed the polling, the dedupe, the
 * three hard-coded endpoints and the bell markup into one file, so none
 * of it could be tested and a fourth source meant editing the render.
 * Here the loop is this hook, the dedupe is `notification-dedupe.ts`, the
 * sources are data (`attention-sources.ts`) and the bell is a component.
 *
 * WHY A BELL AND NOT MORE: Matt's constraint was "not too much burden,
 * doesn't prompt every 10 seconds". The answer is 30 seconds, two
 * sources, and nothing on screen but a count unless the tab is hidden.
 */

/** One thing the operator may need to know about. `key` must be stable
 * for the life of the event — it is what the dedupe remembers, so a key
 * that includes a timestamp or a position would re-announce forever. */
export interface AttentionEvent {
  key: string
  /** The OS notification's heading. Never a message body or a file byte;
   * see `notify.ts`. */
  title: string
  /** The OS notification's line of detail. Same rule. */
  body: string
}

/** Somewhere worth looking every 30 seconds. */
export interface AttentionSource {
  /** Namespaces this source's dedupe keys, so two sources cannot collide
   * on a shared id, and names the source in `failedSourceIds`. */
  id: string
  /** Everything currently outstanding. Resolve with an empty array for
   * "nothing to do"; REJECT for "I could not find out", which is a
   * different thing and is reported as such. */
  load: () => Promise<readonly AttentionEvent[]>
}

export interface AttentionState {
  /** How many items are outstanding across every source that answered.
   * This is the in-app badge, and it is shown whether or not the tab is
   * hidden and whether or not notifications are permitted. */
  count: number
  /** Sources whose most recent poll rejected — an endpoint that is not
   * deployed yet, a 403, a dropped connection.
   *
   * Reported rather than discarded: "there are no pending approvals" and
   * "I could not ask about approvals" look identical in a badge, and an
   * operator relying on a silent watcher deserves to be able to find out
   * which one they are getting. The bell puts this in its tooltip. */
  failedSourceIds: readonly string[]
}

/** D11, exactly. Thirty seconds is slow enough that the tab costs
 * nothing and fast enough that an agent blocked on an approval is not
 * blocked for long. */
export const ATTENTION_POLL_MS = 30_000

const IDLE: AttentionState = { count: 0, failedSourceIds: [] }

/**
 * Polls `sources`, maintains the badge count, and fires an OS
 * notification for each newly-seen event WHILE THE TAB IS HIDDEN.
 *
 * An event seen while the tab is visible is still recorded as seen, and
 * deliberately so: the operator was looking at the badge when it arrived,
 * so buzzing them about it the next time they switch away would be a
 * notification about something they have already been told.
 *
 * `sources` is read through a ref rather than being an effect dependency.
 * The array is rebuilt on every render by any caller that closes over
 * props (which is all of them), so depending on it would tear down and
 * restart the interval several times a second — and an interval that is
 * always restarting never fires. The ref means a changed `rootId` is
 * picked up by the NEXT tick instead of resetting the clock, which is the
 * behaviour we want anyway.
 */
export function useAttentionWatcher(
  sources: readonly AttentionSource[],
): AttentionState {
  const [state, setState] = useState<AttentionState>(IDLE)
  const sourcesRef = useRef(sources)
  sourcesRef.current = sources

  useEffect(() => {
    let isCancelled = false
    const seen = SeenKeyRing.load()

    async function poll(): Promise<void> {
      // Read once per tick, not per source: a tab hidden halfway through
      // a poll should not notify for half its events.
      const isHidden = document.visibilityState === 'hidden'
      let count = 0
      const failedSourceIds: string[] = []

      for (const source of sourcesRef.current) {
        try {
          const events = await source.load()
          count += events.length
          for (const event of events) announce(seen, source.id, event, isHidden)
        } catch {
          // Each source is isolated so one that is not deployed yet, or
          // is refused for this operator, degrades to silence instead of
          // taking the other sources down with it — and instead of
          // becoming an error loop that retries every 30 seconds
          // forever. The failure is surfaced through `failedSourceIds`
          // rather than discarded.
          failedSourceIds.push(source.id)
        }
      }

      seen.persist()
      if (!isCancelled) setState({ count, failedSourceIds })
    }

    const tick = (): void => {
      void poll()
    }

    tick()
    // The interval runs while the tab is HIDDEN, which is the opposite of
    // `use-auto-refresh.ts` and is not an oversight: a hidden tab is the
    // only state in which this hook does its main job. Browsers already
    // throttle background timers to about once a minute, which is a
    // perfectly acceptable floor for a 30-second poll.
    const timer = setInterval(tick, ATTENTION_POLL_MS)
    const onVisibilityChange = (): void => {
      // Both directions. Coming back deserves a fresh count immediately;
      // leaving arms the hidden path so the first thing that arrives
      // after the operator looks away can actually reach them.
      tick()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      isCancelled = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  return state
}

/** Records one event and, if this is the first time we have seen it and
 * the operator is looking elsewhere, puts it on their desktop. */
function announce(
  seen: SeenKeyRing,
  sourceId: string,
  event: AttentionEvent,
  isHidden: boolean,
): void {
  const key = `${sourceId}:${event.key}`
  if (seen.has(key)) return
  seen.add(key)
  if (!isHidden) return
  showNotification(event.title, { body: event.body, tag: key })
}
